import React, { useRef } from 'react';
import { Button } from './ui/button';
import { Camera, X, Image as ImageIcon } from 'lucide-react';

export const ImageUpload = ({ images, setImages, maxImages = 10, disabled = false }) => {
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const remainingSlots = maxImages - images.length;
        
        files.slice(0, remainingSlots).forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setImages(prev => [...prev, {
                        filename: file.name,
                        data: event.target.result,
                        preview: event.target.result
                    }]);
                };
                reader.readAsDataURL(file);
            }
        });
        
        // Reset input
        e.target.value = '';
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                    {images.length} / {maxImages} Bilder
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || images.length >= maxImages}
                    data-testid="add-image-btn"
                >
                    <Camera className="h-4 w-4 mr-2" />
                    Bild hinzufügen
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                />
            </div>

            {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {images.map((img, index) => (
                        <div 
                            key={img.filename || img.preview || `img-${index}`} 
                            className="relative group aspect-square rounded-lg overflow-hidden border bg-muted"
                            data-testid={`image-preview-${img.filename}`}
                        >
                            <img
                                src={img.preview || img.data}
                                alt={img.filename}
                                className="w-full h-full object-cover"
                            />
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`remove-image-${index}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                                {img.filename}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {images.length === 0 && (
                <div 
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => !disabled && fileInputRef.current?.click()}
                >
                    <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                        Klicken oder Bilder hierher ziehen
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Max. {maxImages} Bilder (JPG, PNG)
                    </p>
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
